import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RepuestoFormDialog } from "@/components/RepuestoFormDialog";
import { RepuestoBulkEditDialog } from "@/components/RepuestoBulkEditDialog";
import { CatalogoTabla } from "@/components/CatalogoTabla";
import { GruposEquivalenciaDialog } from "@/components/GruposEquivalenciaDialog";
import { Plus, Download, Upload, Repeat, PencilRuler } from "lucide-react";
import { useBusquedaLive } from "@/lib/busquedaHeader";

export default function Catalogo() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("repuestos.crear");
  const puedeEditar = tienePermiso("repuestos.editar");
  const puedeBorrar = tienePermiso("repuestos.borrar");
  const puedeEquivalencias = tienePermiso("repuestos.equivalencias");
  const puedeCsv = tienePermiso("repuestos.csv");
  const puedeAlgunaAccion = puedeCrear || puedeEditar || puedeEquivalencias || puedeCsv;
  const [repuestos, setRepuestos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [q, setQ] = useBusquedaLive("Buscar en repuestos…");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const uq = searchParams.get("q");
    if (uq !== null) setQ(uq);
  }, [searchParams]);
  const [cat, setCat] = useState("todas");
  const [modelo, setModelo] = useState("todos");
  const [colorF, setColorF] = useState("todos");
  const [colores, setColores] = useState([]);
  const [soloPlastico, setSoloPlastico] = useState(false);
  const [dialog, setDialog] = useState({ open: false, repuesto: null });
  const [seleccion, setSeleccion] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [gruposDialog, setGruposDialog] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [pagina, setPagina] = useState(0);
  const [total, setTotal] = useState(0);
  const POR_PAGINA = 50;
  const fileRef = useRef();

  useEffect(() => { setPagina(0); }, [q, cat, modelo, colorF, soloPlastico]);
  useEffect(() => { setSeleccion([]); }, [q, cat, modelo, colorF, soloPlastico, pagina]);

  const toggleSel = (id) => setSeleccion((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleTodos = (idsPagina, marcar) =>
    setSeleccion((s) => marcar ? Array.from(new Set([...s, ...idsPagina])) : s.filter((x) => !idsPagina.includes(x)));

  const cargar = useCallback(async () => {
    const params = { limit: POR_PAGINA, skip: pagina * POR_PAGINA };
    if (q) params.q = q;
    if (cat !== "todas") params.categoria = cat;
    if (modelo !== "todos") params.modelo_id = modelo;
    if (colorF !== "todos") params.color = colorF;
    if (soloPlastico) params.es_plastico = true;
    const [r, c, g, m, col] = await Promise.all([
      api.get("/repuestos", { params }), api.get("/repuestos/categorias-lista"),
      api.get("/grupos-equivalencia"), api.get("/modelos"), api.get("/repuestos/colores-lista"),
    ]);
    setRepuestos(r.data); setTotal(Number(r.headers["x-total-count"] || r.data.length));
    setCategorias(c.data); setGrupos(g.data); setModelos(m.data); setColores(col.data);
  }, [q, cat, modelo, colorF, soloPlastico, pagina]);

  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este repuesto?")) return;
    try { await api.delete(`/repuestos/${id}`); toast.success("Repuesto eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const exportar = async () => {
    const r = await api.get("/repuestos/exportar-csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "catalogo_repuestos.csv"; a.click();
  };

  const importar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/repuestos/importar-csv", fd);
      toast.success(`Importado: ${data.creados} nuevos, ${data.modelos_agregados} existentes con modelo agregado${data.datos_completados ? `, ${data.datos_completados} con datos completados` : ""}, ${data.sin_cambios} sin cambios`);
      if (data.errores.length) toast.warning(`${data.errores.length} filas con errores: ${data.errores.slice(0, 3).join("; ")}${data.errores.length > 3 ? "..." : ""}`);
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    e.target.value = "";
  };

  const abrir = (r) => setDialog({ open: true, repuesto: r });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Catálogo de repuestos</h1>
          <p className="text-sm text-muted-foreground" data-testid="catalogo-total">{total} repuestos</p>
        </div>
        {puedeAlgunaAccion && (
          <div className="flex flex-wrap gap-2">
            <input type="file" accept=".csv" ref={fileRef} onChange={importar} className="hidden" />
            {seleccion.length > 0 && puedeEditar && (
              <Button data-testid="editar-seleccionados-btn" size="sm" onClick={() => setBulkOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <PencilRuler size={15} className="mr-1" /> Editar seleccionados ({seleccion.length})
              </Button>
            )}
            {puedeEquivalencias && (
              <Button data-testid="grupos-equivalencia-btn" variant="outline" size="sm" onClick={() => setGruposDialog(true)}>
                <Repeat size={15} className="mr-1" /> Grupos de equivalencia
              </Button>
            )}
            {puedeCsv && (
              <Button data-testid="importar-csv-btn" variant="outline" size="sm" onClick={() => fileRef.current.click()}>
                <Upload size={15} className="mr-1" /> Importar CSV
              </Button>
            )}
            {puedeCsv && (
              <Button data-testid="exportar-csv-btn" variant="outline" size="sm" onClick={exportar}>
                <Download size={15} className="mr-1" /> Exportar CSV
              </Button>
            )}
            {puedeCrear && (
              <Button data-testid="nuevo-repuesto-btn" size="sm" onClick={() => abrir(null)}>
                <Plus size={15} className="mr-1" /> Nuevo repuesto
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="filtro-categoria-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={modelo} onValueChange={setModelo}>
          <SelectTrigger data-testid="filtro-modelo-select" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los modelos</SelectItem>
            {modelos.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre_mostrar || `${m.marca} ${m.modelo}`}</SelectItem>)}
          </SelectContent>
        </Select>
        {colores.length > 0 && (
          <Select value={colorF} onValueChange={setColorF}>
            <SelectTrigger data-testid="filtro-color-select" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los colores</SelectItem>
              {colores.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <button type="button" data-testid="filtro-plastico-toggle" onClick={() => setSoloPlastico((v) => !v)}
          className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${soloPlastico
            ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-medium"
            : "border-border bg-white text-muted-foreground hover:text-foreground"}`}>
          Solo plásticos
        </button>
      </div>

      <CatalogoTabla repuestos={repuestos} puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} puedeCrear={puedeCrear}
        onEditar={abrir} onEliminar={eliminar}
        seleccion={seleccion} onToggleSel={toggleSel} onToggleTodos={toggleTodos} />

      {total > POR_PAGINA && (
        <div className="flex items-center justify-end gap-3 text-sm text-slate-600" data-testid="catalogo-paginacion">
          <span data-testid="catalogo-pagina-info">
            {pagina * POR_PAGINA + 1}–{Math.min((pagina + 1) * POR_PAGINA, total)} de {total}
          </span>
          <Button variant="outline" size="sm" data-testid="catalogo-pagina-anterior"
            disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
          <Button variant="outline" size="sm" data-testid="catalogo-pagina-siguiente"
            disabled={(pagina + 1) * POR_PAGINA >= total} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
        </div>
      )}

      <RepuestoFormDialog open={dialog.open} repuesto={dialog.repuesto}
        onOpenChange={(v) => setDialog({ open: v, repuesto: v ? dialog.repuesto : null })} onSaved={cargar} />

      <RepuestoBulkEditDialog open={bulkOpen} ids={seleccion}
        onOpenChange={setBulkOpen} onSaved={() => { setSeleccion([]); cargar(); }} />

      <GruposEquivalenciaDialog open={gruposDialog} onClose={() => setGruposDialog(false)}
        grupos={grupos} onChanged={cargar} />
    </div>
  );
}
